import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, X, Trophy, Lightbulb, MessageSquare, 
  Calendar, HelpCircle, Camera
} from 'lucide-react';
import { Button } from './ui/button';

const QUICK_ACTIONS = [
  { icon: Trophy, label: 'Victoire', path: '/victories', color: 'bg-yellow-500', action: 'victory' },
  { icon: Lightbulb, label: 'Idée', path: '/ideas', color: 'bg-primary', action: 'idea' },
  { icon: Camera, label: 'Story', path: null, color: 'bg-pink-500', action: 'story' },
  { icon: HelpCircle, label: 'Question', path: '/questions', color: 'bg-purple-500', action: 'question' },
  { icon: MessageSquare, label: 'Message', path: '/channels', color: 'bg-blue-500', action: 'message' },
];

const QuickActions = ({ onStoryClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleAction = (action) => {
    setIsOpen(false);
    
    if (action.action === 'story' && onStoryClick) {
      onStoryClick();
    } else if (action.path) {
      navigate(action.path, { state: { openCreate: true } });
    }
  };

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Quick Actions Menu */}
      <div className="fixed bottom-[88px] right-4 z-50 md:bottom-6">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-16 right-0 space-y-2"
            >
              {QUICK_ACTIONS.map((action, index) => {
                const Icon = action.icon;
                return (
                  <motion.div
                    key={action.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 justify-end"
                  >
                    <span className="px-3 py-1.5 bg-card rounded-lg text-sm font-medium shadow-lg border border-border">
                      {action.label}
                    </span>
                    <button
                      onClick={() => handleAction(action)}
                      className={`w-12 h-12 rounded-full ${action.color} text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform`}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main FAB Button */}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
            isOpen 
              ? 'bg-destructive text-white rotate-45' 
              : 'bg-primary text-primary-foreground gold-glow'
          }`}
          data-testid="quick-action-fab"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
        </motion.button>
      </div>
    </>
  );
};

export default QuickActions;
